// A mapping from ISO 3166-1 alpha-2 country codes to ISO 4217 currency codes.
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  // Major Markets
  US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
  // Europe (Eurozone)
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR', IE: 'EUR', PT: 'EUR', FI: 'EUR', GR: 'EUR',
  // Europe (Non-Euro)
  CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON',
  // Middle East
  AE: 'AED', SA: 'SAR', QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', IL: 'ILS', TR: 'TRY',
  // Asia
  IN: 'INR', JP: 'JPY', CN: 'CNY', KR: 'KRW', SG: 'SGD', HK: 'HKD', MY: 'MYR', TH: 'THB', ID: 'IDR', PH: 'PHP', VN: 'VND', PK: 'PKR', BD: 'BDT',
  // Africa
  ZA: 'ZAR', NG: 'NGN', EG: 'EGP', KE: 'KES', MA: 'MAD',
  // Latin America
  MX: 'MXN', BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN',
};

/**
 * Gets the ISO 4217 currency code for a given ISO 3166-1 alpha-2 country code.
 * @param countryCode The 2-letter country code (e.g., "GB").
 * @returns The 3-letter currency code (e.g., "GBP"), defaulting to "USD".
 */
export const getCurrencyForCountry = (countryCode: string | null | undefined): string => {
  if (!countryCode) return 'USD'; // Default fallback
  return COUNTRY_TO_CURRENCY[countryCode.toUpperCase()] || 'USD';
};

/**
 * Formats a numeric price into a localized currency string.
 * @param price The numeric price to format.
 * @param countryCode The 2-letter country code of the restaurant's location.
 * @returns A formatted currency string (e.g., "£14.95", "AED 55.00").
 */
export const formatPrice = (
  price: number | null | undefined,
  countryCode: string | null | undefined
): string => {
  if (price === null || price === undefined) return '';

  const currency = getCurrencyForCountry(countryCode);

  try {
    // Use the browser's Intl API for robust, zero-dependency formatting.
    // 'undefined' for the locale lets the user's browser decide on number formatting (e.g., comma vs. period).
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2, // Standard for most currencies
    }).format(price);
  } catch (error) {
    console.error(`Failed to format currency for code: ${currency}`, error);
    // Fallback for an invalid currency code, though this should be rare with our map.
    return `${price.toFixed(2)}`;
  }
};
